/**
 * Module Permissions Configuration
 *
 * Defines all available modules in the system.
 * User permissions are stored as an array of module keys.
 * Empty array [] = NO access (every menu item is filtered out).
 * Server-side `getUserPermissions` already grants is_company_admin users
 * the full company set, so the array is always populated for valid users.
 */

// In-app chat (the /chat screen) is switched off unless REACT_APP_IN_APP_CHAT_ENABLED=true (owner 27 Oct 2026).
// The backend refuses the chat screen's endpoints the same way (backend/chat/in-app-chat-switch.js).
export const IN_APP_CHAT_ENABLED = process.env.REACT_APP_IN_APP_CHAT_ENABLED === 'true';

export const MODULES = {
  // Core modules
  DASHBOARD: 'dashboard',
  BRANDS: 'brands',
  CONNECTORS: 'connectors',
  AUTOMATION: 'automation',
  CHAT: 'chat',
  EXPERTS: 'experts',
  SCHEDULED_JOBS: 'scheduled_jobs',
  VISUALIZATIONS: 'visualizations',
  ADMIN: 'admin',
  CALL_CENTRE: 'call_centre',

  // Inventory modules
  INVENTORY: 'inventory',
  INVENTORY_STOCK: 'inventory_stock',
  INVENTORY_BATCHES: 'inventory_batches',
  INVENTORY_REPORTS: 'inventory_reports',
  INVENTORY_COGS: 'inventory_cogs',
  INVENTORY_WAREHOUSES: 'inventory_warehouses',
  INVENTORY_MAPPINGS: 'inventory_mappings',
  INVENTORY_TRANSFERS: 'inventory_transfers',
  INVENTORY_RETURNS: 'inventory_returns',
  INVENTORY_DISPOSALS: 'inventory_disposals',
  INVENTORY_COUNTS: 'inventory_counts',
  INVENTORY_FBA: 'inventory_fba',
  INVENTORY_PACKAGING: 'inventory_packaging',

  // Procurement modules
  PROCUREMENT: 'procurement',
  PROCUREMENT_SUPPLIERS: 'procurement_suppliers',
  PROCUREMENT_PO: 'procurement_po',
  PROCUREMENT_RECEIPTS: 'procurement_receipts',
  PROCUREMENT_INVOICES: 'procurement_invoices',
  PROCUREMENT_PAYMENTS: 'procurement_payments',

  // Retail modules
  RETAIL: 'retail',
  RETAIL_ORDERS: 'retail_orders',

  // Finance modules
  FINANCE: 'finance',
  FINANCE_CASH: 'finance_cash',
  FINANCE_AR: 'finance_ar',
  FINANCE_AP: 'finance_ap',
  FINANCE_PL: 'finance_pl',
  FINANCE_INTERCOMPANY: 'finance_intercompany',
  FINANCE_RECONCILIATION: 'finance_reconciliation',
  FINANCE_SETTINGS: 'finance_settings',
  FINANCE_STATEMENTS: 'finance_statements',
};

/**
 * Check if user has access to a module
 * @param {Array} userPermissions - User's permission array (checked modules = has access)
 * @param {String} moduleKey - Module key to check
 * @returns {Boolean} - True if user has access
 */
export const hasModuleAccess = (userPermissions, moduleKey) => {
  // If no permissions array, default to no access
  if (!userPermissions) {
    return false;
  }

  // Empty array = no access
  // Non-empty array = has access to modules in the array
  return userPermissions.includes(moduleKey);
};

/**
 * Check if user has access to any module in a category
 * Used for showing parent menu items when user has access to at least one child
 * @param {Array} userPermissions - User's permission array
 * @param {Array} moduleKeys - Array of module keys to check
 * @returns {Boolean} - True if user has access to at least one module
 */
export const hasAnyModuleAccess = (userPermissions, moduleKeys) => {
  // If no permissions, no access
  if (!userPermissions) {
    return false;
  }

  // Check if user has at least one permission from the list
  return moduleKeys.some(key => userPermissions.includes(key));
};

/**
 * Filter menu items based on user permissions
 * @param {Array} menuItems - Array of menu items
 * @param {Array} userPermissions - User's permission array
 * @returns {Array} - Filtered menu items
 */
export const filterMenuByPermissions = (menuItems, userPermissions) => {
  // If no permissions, return empty (no access)
  if (!userPermissions) {
    return [];
  }

  return menuItems.filter(item => {
    // If item has a moduleKey, check permission
    if (item.moduleKey) {
      return hasModuleAccess(userPermissions, item.moduleKey);
    }
    // Items without moduleKey are always shown (like dividers)
    return true;
  });
};

// Modules defined in MODULES for future use but not yet exposed in the admin UI.
// Their sidebar entries are commented-out in Layout.js ("not fully implemented").
// When those entries get uncommented, add the keys to MODULE_GROUPS at the same time.
export const HIDDEN_MODULES = [MODULES.AUTOMATION, MODULES.VISUALIZATIONS];

// Single source of truth for the admin checkbox UIs.
// Used by PermissionsDialog (per-user) and CompanyModulesDialog (per-company).
// Adding a new module = one entry in MODULES + one row here.
export const MODULE_GROUPS = [
  { label: 'Core', modules: [
    { key: MODULES.DASHBOARD,       label: 'Dashboard' },
    { key: MODULES.BRANDS,          label: 'Brands' },
    { key: MODULES.CONNECTORS,      label: 'Connectors' },
    { key: MODULES.CHAT,            label: 'Chat' },
    { key: MODULES.EXPERTS,         label: 'AI Agents' },
    { key: MODULES.SCHEDULED_JOBS,  label: 'Scheduled Jobs' },
    { key: MODULES.CALL_CENTRE,     label: 'Call Centre' },
    { key: MODULES.ADMIN,           label: 'Admin Panel' },
  ]},
  { label: 'Inventory', modules: [
    { key: MODULES.INVENTORY,             label: 'Overview' },
    { key: MODULES.INVENTORY_STOCK,       label: 'Stock Levels' },
    { key: MODULES.INVENTORY_BATCHES,     label: 'Batches' },
    { key: MODULES.INVENTORY_REPORTS,     label: 'Reports' },
    { key: MODULES.INVENTORY_COGS,        label: 'Product COGS' },
    { key: MODULES.INVENTORY_WAREHOUSES,  label: 'Warehouses' },
    { key: MODULES.INVENTORY_MAPPINGS,    label: 'Store Mappings' },
    { key: MODULES.INVENTORY_TRANSFERS,   label: 'Transfers' },
    { key: MODULES.INVENTORY_RETURNS,     label: 'Returns' },
    { key: MODULES.INVENTORY_DISPOSALS,   label: 'Disposals' },
    { key: MODULES.INVENTORY_COUNTS,      label: 'Physical Counts' },
    { key: MODULES.INVENTORY_FBA,         label: 'FBA Discrepancies' },
    { key: MODULES.INVENTORY_PACKAGING,   label: 'Packaging Consumables' },
  ]},
  { label: 'Procurement', modules: [
    { key: MODULES.PROCUREMENT,           label: 'Overview' },
    { key: MODULES.PROCUREMENT_SUPPLIERS, label: 'Suppliers' },
    { key: MODULES.PROCUREMENT_PO,        label: 'Purchase Orders' },
    { key: MODULES.PROCUREMENT_RECEIPTS,  label: 'Receipts' },
    { key: MODULES.PROCUREMENT_INVOICES,  label: 'Invoices' },
    { key: MODULES.PROCUREMENT_PAYMENTS,  label: 'Payments' },
  ]},
  { label: 'Retail', modules: [
    { key: MODULES.RETAIL,         label: 'Overview' },
    { key: MODULES.RETAIL_ORDERS,  label: 'Orders' },
  ]},
  { label: 'Finance', modules: [
    { key: MODULES.FINANCE,                label: 'Overview' },
    { key: MODULES.FINANCE_CASH,           label: 'Cash Position' },
    { key: MODULES.FINANCE_AR,             label: 'AR Analysis' },
    { key: MODULES.FINANCE_AP,             label: 'AP Analysis' },
    { key: MODULES.FINANCE_PL,             label: 'P&L Reports' },
    { key: MODULES.FINANCE_INTERCOMPANY,   label: 'Intercompany' },
    { key: MODULES.FINANCE_RECONCILIATION, label: 'Reconciliation' },
    { key: MODULES.FINANCE_SETTINGS,       label: 'Finance Settings' },
    { key: MODULES.FINANCE_STATEMENTS,     label: 'Financial Statements' },
  ]},
];

// Helper: flat list of all currently-shown module keys (excludes HIDDEN_MODULES).
// Use this whenever you previously used Object.values(MODULES) in admin UI.
export const VISIBLE_MODULE_KEYS = MODULE_GROUPS
  .flatMap(g => g.modules.map(m => m.key));
